import { CreveLogo } from '@/components/creve/CreveLogo';
import { Button } from '@/components/ui/button';
import { WalletButton } from '@/components/WalletButton';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyCartItems, CartItemsResponse } from '@/lib/collections/cartItems';
import { useAuth } from '@pooflabs/web';
import { LogIn, ShoppingCart, User, UserPlus } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const LandingHeader: React.FC = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const { data: cartItems } = useRealtimeData<CartItemsResponse[]>(
    subscribeManyCartItems,
    !!user?.address,
    user?.address ? `where owner is '${user.address}'` : ''
  );
  const cartCount = cartItems?.length ?? 0;

  const handleAuth = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Failed to login', error);
      toast.error('Could not open wallet login. Please try again.');
    }
  };

  return (
    <header className='sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md'>
      <div className='container flex h-16 items-center justify-between'>
        <CreveLogo />

        <div className='flex items-center gap-2 sm:gap-3'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => navigate('/cart')}
            className='relative rounded-full text-foreground hover:bg-secondary hover:text-primary'
            aria-label='Cart'
          >
            <ShoppingCart className='h-5 w-5' />
            {!!user && cartCount > 0 && (
              <span className='absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-primary-foreground'>
                {cartCount}
              </span>
            )}
          </Button>

          {user ? (
            <>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => navigate('/profile/edit')}
                className='rounded-full text-foreground hover:bg-secondary hover:text-primary'
                aria-label='Edit profile'
              >
                <User className='h-5 w-5' />
              </Button>
              <WalletButton />
            </>
          ) : (
            <>
              <Button
                variant='ghost'
                onClick={handleAuth}
                className='gap-2 rounded-full px-3 text-foreground hover:bg-secondary hover:text-primary sm:px-4'
              >
                <LogIn className='h-4 w-4' />
                <span className='hidden sm:inline'>Log in</span>
              </Button>
              <Button
                onClick={handleAuth}
                className='gap-2 rounded-full bg-primary px-3 text-primary-foreground hover:bg-primary/90 sm:px-5'
              >
                <UserPlus className='h-4 w-4' />
                <span className='hidden sm:inline'>Sign up</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default LandingHeader;
